/**
 * ESCHER FALLS — 音の質感を作る部品。
 *
 * 初版は「雑音を濾して包絡線を掛ける」だけだった。それだと
 * **石はシュッという掠れ音、水は砂嵐**になる。自然に聞こえない理由は3つあり、
 * ここの部品はその3つをそれぞれ埋めるためにある。
 *
 * ### 1. 実際の摩擦音は滑らかな雑音ではない
 * 石が擦れる音は、無数の微小な衝突の連なりである。連続した雑音のままだと
 * 布が擦れる音になる。→ `grainCloud`（粒を不揃いな間隔で撒く）
 *
 * ### 2. 質量は低音の「胴鳴り」で伝わる
 * 重さは音量ではなく、低い帯域の共振と、立ち上がりの遅さで決まる。
 * 石板には固有の共振があるので、高い Q の帯域を数本重ねる。
 * → `resonantBody`
 *
 * ### 3. 水は「泡」で水になる
 * 帯域雑音をいくら揺らしても風の音にしかならない。水だと分かる決め手は、
 * **短く音程が上がる泡の音**が不規則に混ざることである。→ `bubble`
 *
 * また、揺らぎに正弦波の LFO を使うと「ワウワウ」という人工的な周期が出る。
 * 自然な揺らぎは周期を持たないので、なめらかな乱数の波を使う。→ `slowRandom`
 *
 * 乱数は**すべて決定的**。版ごとに鳴り方が変わると「前より耳障りだ」の
 * 原因を追えなくなる。
 */

import { noise } from './engine';

let seed = 0x13c6ef37;

/** 決定的な擬似乱数 0..1。 */
export function rnd(): number {
  seed = (seed * 1103515245 + 12345) & 0x7fffffff;
  return seed / 0x7fffffff;
}

/** `lo`〜`hi` の乱数。 */
export function between(lo: number, hi: number): number {
  return lo + (hi - lo) * rnd();
}

/** ループする白色雑音源。 */
export function noiseSource(ctx: AudioContext): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx);
  src.loop = true;
  return src;
}

/**
 * なめらかな乱数の波。揺らぎの元にする。
 *
 * 正弦波の LFO だと周期が耳につく（「ワウワウ」と鳴って人工的になる）。
 * 折れ線を補間した乱数にすると周期が消え、自然なゆらぎになる。
 *
 * @param rateHz 1秒あたり何個の節を打つか。小さいほどゆっくり漂う。
 */
export function slowRandom(ctx: AudioContext, seconds: number, rateHz: number): AudioBuffer {
  const len = Math.max(2, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  const step = Math.max(2, Math.floor(ctx.sampleRate / rateHz));
  let prev = rnd() * 2 - 1;
  let next = rnd() * 2 - 1;
  for (let i = 0; i < len; i += 1) {
    const k = i % step;
    if (k === 0) {
      prev = next;
      next = rnd() * 2 - 1;
    }
    // 余弦で滑らかに繋ぐ。線形だと節で折れて、その折れ目が聞こえる。
    const u = (1 - Math.cos((k / step) * Math.PI)) / 2;
    data[i] = prev + (next - prev) * u;
  }
  return buf;
}

/**
 * 揺らぎを AudioParam に繋ぐ。ループするので長く鳴らしても周期が現れない。
 * @returns 停止用のノード
 */
export function modulate(
  ctx: AudioContext,
  param: AudioParam,
  opts: { depth: number; rateHz: number; seconds?: number; at: number },
): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = slowRandom(ctx, opts.seconds ?? 9, opts.rateHz);
  src.loop = true;
  const depth = ctx.createGain();
  depth.gain.value = opts.depth;
  src.connect(depth).connect(param);
  src.start(opts.at);
  return src;
}

/**
 * 粒の雲。**摩擦音の正体はこれである。**
 *
 * 短い雑音の粒を不揃いな間隔・不揃いな大きさで撒く。
 * 連続した雑音との差は決定的で、粒にすると初めて「ざらざらと擦れている」
 * ものとして聞こえる。等間隔にすると機械の音になるので必ず揺らす。
 */
export function grainCloud(
  ctx: AudioContext,
  out: AudioNode,
  opts: {
    at: number;
    dur: number;
    /** 粒の数。多いほど滑らか、少ないほど粗い */
    count: number;
    level: number;
    /** 粒を通す濾波器の中心（既定は低域通過の上限） */
    cutoff: number;
    /** 粒の長さ (s) */
    grain?: number;
    /** 濾波器の種類。擦れの粒立ちを足すときは bandpass にする */
    type?: BiquadFilterType;
    q?: number;
  },
): void {
  const grain = opts.grain ?? 0.014;
  const filter = ctx.createBiquadFilter();
  filter.type = opts.type ?? 'lowpass';
  filter.frequency.value = opts.cutoff;
  filter.Q.value = opts.q ?? 0.7;
  filter.connect(out);

  for (let i = 0; i < opts.count; i += 1) {
    // 位置を揺らす。等間隔だと拍が生まれて機械音になる。
    const u = (i + between(-0.4, 0.4)) / opts.count;
    if (u < 0 || u >= 1) continue;
    const at = opts.at + u * opts.dur;
    // 中盤を強くする。擦り始めと擦り終わりは弱い。
    const shape = Math.sin(Math.PI * u) ** 0.7;
    const peak = opts.level * shape * between(0.35, 1);

    const src = ctx.createBufferSource();
    src.buffer = noise(ctx);
    // 粒ごとに読み出し位置を変える。同じ波形の繰り返しは金属的に響く。
    const offset = between(0, 1.8);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), at + grain * 0.25);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + grain);
    src.connect(gain).connect(filter);
    src.start(at, offset, grain + 0.01);
    src.stop(at + grain + 0.02);
  }
}

/**
 * 胴鳴り。**質量はここで決まる。**
 *
 * 高い Q の帯域を数本重ね、立ち上がりを遅くする。
 * 速く立ち上げると軽い物になるので、重さを出したいときは必ず遅くする。
 *
 * ## 倍音を必ず足すこと（`harmonics`）
 * **スマートフォンのスピーカーは 300〜500Hz より下をほとんど鳴らせない。**
 * 基音だけで重さを作ると、机の上では立派に鳴るのに実機では消える
 * （実際に「音量が小さい」と報告された。小さいのではなく、
 * 重さを担っている帯域が丸ごと再生されていなかった）。
 *
 * 2倍・3倍の音を足すと、耳が**存在しない基音を補って聞く**（missing fundamental）。
 * 小さなスピーカーでも「低く重い」と感じられるのはこの働きによる。
 * イヤホンでは本物の低音も一緒に鳴るので、どちらでも成り立つ。
 */
export function resonantBody(
  ctx: AudioContext,
  out: AudioNode,
  opts: {
    at: number;
    /** 共振の周波数。石板らしさは 80〜250Hz あたりに出る */
    freqs: readonly number[];
    level: number;
    attack: number;
    decay: number;
    q?: number;
    /**
     * 倍音の段数と強さ。`[2倍の強さ, 3倍の強さ, ...]`。
     * 小さなスピーカーで重さを残すために既定でも足す。
     */
    harmonics?: readonly number[];
  },
): void {
  const src = noiseSource(ctx);
  const bus = ctx.createGain();
  bus.gain.setValueAtTime(0.0001, opts.at);
  bus.gain.exponentialRampToValueAtTime(Math.max(0.0001, opts.level), opts.at + opts.attack);
  bus.gain.exponentialRampToValueAtTime(0.0001, opts.at + opts.attack + opts.decay);
  bus.connect(out);

  const harmonics = opts.harmonics ?? [0.9, 0.7, 0.45];
  for (const freq of opts.freqs) {
    // 基音
    const peak = ctx.createBiquadFilter();
    peak.type = 'bandpass';
    peak.frequency.value = freq;
    peak.Q.value = opts.q ?? 7;
    src.connect(peak).connect(bus);

    // 倍音。**これが実機で聞こえる部分である。**
    harmonics.forEach((amp, i) => {
      const mult = i + 2; // 2倍, 3倍, 4倍...
      const hp = ctx.createBiquadFilter();
      hp.type = 'bandpass';
      hp.frequency.value = freq * mult;
      hp.Q.value = (opts.q ?? 7) * 0.8;
      const hg = ctx.createGain();
      hg.gain.value = amp;
      src.connect(hp).connect(hg).connect(bus);
    });
  }
  src.start(opts.at, between(0, 1.5));
  src.stop(opts.at + opts.attack + opts.decay + 0.1);
}

/**
 * 鈍い衝突。石が止まって噛み合う音。
 * 減衰する低い正弦（＝叩かれた塊）と、ごく短い雑音（＝接触の瞬間）を重ねる。
 */
export function thud(
  ctx: AudioContext,
  out: AudioNode,
  opts: { at: number; level: number; freq: number; decay?: number },
): void {
  const decay = opts.decay ?? 0.22;

  // 基音と倍音2本。倍音が無いと、小さなスピーカーでは衝撃が消えて
  // 「カチッ」という接触音だけが残る（重さが伝わらない）。
  for (const [mult, amp] of [[1, 1], [2, 0.55], [3, 0.28]] as Array<[number, number]>) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(opts.freq * mult, opts.at);
    // わずかに下がる。一定だと音程として聞こえてしまう。
    osc.frequency.exponentialRampToValueAtTime(opts.freq * mult * 0.7, opts.at + decay);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, opts.at);
    g.gain.exponentialRampToValueAtTime(opts.level * amp, opts.at + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, opts.at + decay);
    osc.connect(g).connect(out);
    osc.start(opts.at);
    osc.stop(opts.at + decay + 0.05);
  }

  const click = ctx.createBufferSource();
  click.buffer = noise(ctx);
  const cf = ctx.createBiquadFilter();
  cf.type = 'lowpass';
  cf.frequency.value = 1200;
  const cg = ctx.createGain();
  cg.gain.setValueAtTime(opts.level * 0.6, opts.at);
  cg.gain.exponentialRampToValueAtTime(0.0001, opts.at + 0.035);
  click.connect(cf).connect(cg).connect(out);
  click.start(opts.at, between(0, 1.5), 0.06);
  click.stop(opts.at + 0.06);
}

/**
 * 泡。**水だと分かる決め手はこれである。**
 *
 * 短く、音程が**上がって**消える。上がるのが要点で、下げると水滴が落ちる音、
 * 上げると水中で泡が弾ける音になる。滝の音はこれが無数に混ざったもの。
 */
export function bubble(
  ctx: AudioContext,
  out: AudioNode,
  opts: { at: number; level: number; from?: number; dur?: number },
): void {
  const dur = opts.dur ?? 0.05;
  const from = opts.from ?? between(240, 900);
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(from, opts.at);
  osc.frequency.exponentialRampToValueAtTime(from * between(1.6, 2.6), opts.at + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, opts.at);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, opts.level), opts.at + dur * 0.25);
  g.gain.exponentialRampToValueAtTime(0.0001, opts.at + dur);
  osc.connect(g).connect(out);
  osc.start(opts.at);
  osc.stop(opts.at + dur + 0.02);
}
